import React, { useState } from 'react';

/**
 * UserBadge — small Twitch-style pill badge rendered next to a username.
 * Currently supports the "OG 50" Founding Member badge and the BEATSEEK admin badge.
 *
 * Usage:
 *   <UserBadge type="original_40" />
 *   <UserBadge type="admin" />
 *   <UserBadge type="original_40" size="sm" />  // sm | md
 */
const GROOVLENS_LOGO_URL = '/groovlens-badge.png';

// Sub-component so we can use useState for image-error fallback (letter "G")
const CircularBadge = ({ cfg, type, size, className }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const diameter = size === 'sm' ? 20 : 26;
  const fallbackLetter = (cfg.fallbackChar || (cfg.title || '?').charAt(0)).toUpperCase();

  return (
    <span
      className={`user-badge user-badge-${type} ${className}`}
      title={cfg.title}
      data-testid={`user-badge-${type}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: `${diameter}px`,
        height: `${diameter}px`,
        borderRadius: '50%',
        background: cfg.fallbackBg || 'linear-gradient(135deg, #06b6d4 0%, #8b5cf6 100%)',
        boxShadow: '0 0 6px rgba(6, 182, 212, 0.55), 0 0 10px rgba(139, 92, 246, 0.35)',
        verticalAlign: 'middle',
        flexShrink: 0,
        userSelect: 'none',
        overflow: 'hidden',
        color: '#fff',
        fontWeight: 800,
        fontSize: size === 'sm' ? '8px' : '10px',
        letterSpacing: '0.02em',
      }}
    >
      {!imgFailed && cfg.image ? (
        <img
          src={cfg.image}
          alt={cfg.title}
          referrerPolicy="no-referrer"
          onError={() => setImgFailed(true)}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            display: 'block',
          }}
          draggable={false}
        />
      ) : (
        <span aria-hidden="true">{fallbackLetter}</span>
      )}
    </span>
  );
};

const BADGE_CONFIG = {
  original_40: {
    shape: 'pill',
    label: 'OG 50',
    title: 'OG 50 — Founding Member',
    bg: 'linear-gradient(135deg, #ff00ff 0%, #c026d3 50%, #06b6d4 100%)',
    icon: '\uD83C\uDF1F',
  },
  admin: {
    shape: 'pill',
    label: 'ADMIN',
    title: 'BEATSEEK Admin',
    bg: 'linear-gradient(135deg, #06b6d4 0%, #8b5cf6 50%, #c026d3 100%)',
    icon: '\u2728',
  },
  creator_of_week: {
    shape: 'pill',
    label: 'CREATOR OF WEEK',
    title: 'BEATSEEK Creator of the Week',
    bg: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #c026d3 100%)',
    icon: '\uD83C\uDFC6', // 🏆
  },
  past_creator_of_week: {
    shape: 'pill',
    label: 'PAST WEEKLY CREATOR',
    title: 'Past Creator of the Week',
    bg: 'linear-gradient(135deg, #475569 0%, #64748b 100%)',
    icon: '\uD83C\uDFC5', // 🏅
  },
  groovlens: {
    shape: 'circle',
    title: 'Also on GroovLens by BEATSEEK',
    image: GROOVLENS_LOGO_URL,
    fallbackChar: 'G',
    fallbackBg: 'linear-gradient(135deg, #06b6d4 0%, #8b5cf6 100%)',
  },
  vip: {
    shape: 'pill',
    label: 'VIP',
    title: 'VIP — Lifetime Member',
    bg: 'linear-gradient(135deg, #f7d55c 0%, #e0a92f 50%, #b8860b 100%)',
    icon: '\uD83D\uDC51', // 👑
  },
};

const UserBadge = ({ type, size = 'md', className = '' }) => {
  const cfg = BADGE_CONFIG[type];
  if (!cfg) return null;

  // Circular logo-only badge (e.g., GroovLens)
  if (cfg.shape === 'circle') {
    return (
      <CircularBadge
        cfg={cfg}
        type={type}
        size={size}
        className={className}
      />
    );
  }

  const sizeStyles = size === 'sm'
    ? { padding: '1px 5px', fontSize: '8px', borderRadius: '4px', gap: '2px' }
    : { padding: '2px 7px', fontSize: '10px', borderRadius: '6px', gap: '3px' };

  return (
    <span
      className={`user-badge user-badge-${type} ${className}`}
      title={cfg.title}
      data-testid={`user-badge-${type}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        ...sizeStyles,
        background: cfg.bg,
        color: '#fff',
        fontWeight: 800,
        letterSpacing: '0.04em',
        textShadow: '0 1px 2px rgba(0,0,0,0.6)',
        boxShadow: '0 0 6px rgba(255, 0, 255, 0.45), 0 0 10px rgba(6, 182, 212, 0.25)',
        verticalAlign: 'middle',
        whiteSpace: 'nowrap',
        flexShrink: 0,
        userSelect: 'none',
        lineHeight: 1,
      }}
    >
      <span style={{ fontSize: size === 'sm' ? '8px' : '10px' }}>{cfg.icon}</span>
      {cfg.label}
    </span>
  );
};

/**
 * UserBadgeGroup — render all badges a user has, side by side.
 * Pass user.badges array (e.g., ['original_40']).
 */
export const UserBadgeGroup = ({ badges, size = 'md', gap = 4 }) => {
  if (!badges || badges.length === 0) return null;
  return (
    <span style={{ display: 'inline-flex', gap: `${gap}px`, alignItems: 'center', flexShrink: 0 }}>
      {badges.map((b) => (
        <UserBadge key={b} type={b} size={size} />
      ))}
    </span>
  );
};

export default UserBadge;
